import { useAppSelector } from "../hooks/redux";
import { routes } from "./public/routes";
import { AppUpdateModal } from "../components/CustomComponents/AppUpdateModal";
import { APP_ENV } from "../constants/constant";
import {
  analyticsController,
  circadiaEngineProvider,
  localStorageProvider,
  timeProvider,
} from "../AppController";
import { AppRouter } from "./appRouter";
import { featureFlag } from "../constants/feature";
import { useNetworkStatus } from "../hooks/network";
import { useStatsTimeout } from "../hooks/useStatsTimeout";
// import VersionChecker from "../version";
import { ScreenLoaderWhite } from "../components/CustomComponents/ScreenLoaderWhite";
import { useEffect, useRef, useState } from "react";
import { triggerIntercomOnLoginSuccess } from "../util/intercomTrigger";
import VitalsRedirectHandler from "../components/VitalsRedirectHandler";
import { useVitalsSubdomainRedirect } from "../hooks/useVitalsSubdomainRedirect";
import { getSavedConnectDeviceUrl } from "../utils/connectDeviceRestore";

// CIRCADIA-PERF: Bed-exit chime disabled — facility feature, not used in patient app.
// Allocating an Audio element with loop=true keeps the iOS audio session active,
// causing high energy impact even when nothing is playing.
// To re-enable: uncomment the two lines below.
// window.chimeAudio = new Audio("/assets/audio/bed_exit_chime.wav");
// window.chimeAudio.loop = true;
window.chimeAudio = { play() {}, pause() {}, loop: false } as any;

const Router = () => {
  // Handle vitals subdomain redirect - must be called first
  useVitalsSubdomainRedirect();

  useNetworkStatus({
    analyticsController,
    localStorageProvider,
    timeProvider,
  });
  useStatsTimeout();
  const { authState, isAuthorized, layoutState, user } = useAppSelector(
    (state) => {
      return {
        authState: state.auth.authState,
        isAuthorized: state.auth.isAuthorized,
        isPinAuthorized: state.auth.isPinAuthorized,
        layoutState: state.layout.layoutState,
        user: state.auth.user,
      };
    }
  );

  // When on any protected (mobile) route but not authorized, show loader so rehydration can complete
  // without losing the URL. If still not authorized after 4s, allow render so logged-out users can reach login.
  const [protectedRouteWaitExpired, setProtectedRouteWaitExpired] =
    useState(false);
  const hashSlice =
    typeof window !== "undefined" ? window.location.hash.slice(1) || "" : "";
  const urlFromHash = hashSlice.split("?")[0];
  const publicPaths = Object.entries(routes).map(
    ([_k, v]) => (v as { path: string }).path
  );
  const isOnProtectedRoute =
    urlFromHash && urlFromHash !== "/" && !publicPaths.includes(urlFromHash);

  useEffect(() => {
    if (!isOnProtectedRoute || isAuthorized) return;
    const t = setTimeout(() => setProtectedRouteWaitExpired(true), 4000);
    return () => clearTimeout(t);
  }, [isOnProtectedRoute, isAuthorized]);

  // Hide Capacitor splash screen once the app is ready to render.
  // Login/GetStarted pages handle their own hide (after bg image loads);
  // for already-authorized users we hide here. A 10s timeout guarantees
  // the splash never gets stuck on extremely slow connections.
  const splashHiddenRef = useRef(false);
  useEffect(() => {
    const hideSplash = () => {
      if (splashHiddenRef.current) return;
      splashHiddenRef.current = true;
      (window as any).Capacitor?.Plugins?.SplashScreen?.hide();
    };

    if (isAuthorized) {
      hideSplash();
    }

    const timeout = setTimeout(hideSplash, 10000);
    return () => clearTimeout(timeout);
  }, [isAuthorized]);

  // Intercom auto-trigger after 20 seconds when user is logged in
  useEffect(() => {
    if (user && isAuthorized) {
      const timer = setTimeout(() => {
        const currentPath = window.location.hash || window.location.pathname;
        const shouldExclude = currentPath.includes("#/dashboard?patientIds");

        if (!shouldExclude) {
          triggerIntercomOnLoginSuccess();
        }
      }, 20000);

      return () => clearTimeout(timer);
    }
  }, [user, isAuthorized]);

  const getLayoutToRender = () => {
    const hashSlice = window.location.hash.slice(1) || "";
    const url = hashSlice.split("?")[0];

    if (
      !isAuthorized &&
      !Object.entries(routes)
        .map(([_key, value]) => value.path)
        // @ts-ignore
        .includes(url)
    ) {
      localStorageProvider.set("prevUrl", hashSlice);
    }

    // When on any protected (mobile) route: show loader until we know auth state, so we never hand off
    // to the public router and lose the URL. If still not authorized after 4s, allow render so logged-out users can reach login.
    if (isOnProtectedRoute) {
      if (layoutState === "loading" || authState === "loading") {
        return <ScreenLoaderWhite />;
      }
      if (!isAuthorized && !protectedRouteWaitExpired) {
        return <ScreenLoaderWhite />;
      }
    }

    if (layoutState === "loading" || authState === "loading") {
      return <ScreenLoaderWhite />;
    }

    // If we're authorized but the URL was changed to login/empty (e.g. public router ran),
    // restore connect-device so we land on the right page after reload
    if (isAuthorized) {
      const currentPath = (
        typeof window !== "undefined" ? window.location.hash.slice(1) : ""
      ).split("?")[0];
      const entryPaths = ["", "/", "/login"];
      if (entryPaths.includes(currentPath)) {
        const saved = getSavedConnectDeviceUrl();
        if (saved) {
          const target = saved.startsWith("#") ? saved : `#${saved}`;
          window.location.replace(
            `${window.location.origin}${
              window.location.pathname || ""
            }${target}`
          );
          return <ScreenLoaderWhite />;
        }
      }
    }

    return <AppRouter />;
  };

  const showUpdateModal =
    APP_ENV === "prod" &&
    circadiaEngineProvider.plat === "ios" &&
    circadiaEngineProvider.intfv < 4 &&
    featureFlag.appUpdateModal; // feature flag

  return (
    <>
      {/* removing version checker for this release */}
      {/* {window.location.hostname !== "localhost" && <VersionChecker />} */}
      {/* <AppPerformanceSurvey /> */}
      <AppUpdateModal visible={showUpdateModal} />
      <VitalsRedirectHandler />
      {getLayoutToRender()}
    </>
  );
};

export default Router;
